import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Loader2, Rocket } from "lucide-react";
import toast from "react-hot-toast";

import { useAuth } from "@/contexts/AuthContext";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import { getFiscaloConfigRunning, startFiscaloConfig } from "@/services/fiscalService";

/**
 * Dashboard card — final self-service activation. Once the prerequisites are
 * real and working (certificate + CAE + confirmed e-Factura user), the owner
 * taps to register Fiscalo as their DGI software provider (idemisor_sync →
 * proveedor_software). Shows Ready / Running / Failed and disappears once
 * `emisionEnabled` (provider registered → real emission unblocked). Hidden for
 * guest accountants and until the prerequisites are met.
 */
export function DashboardActivateFiscaloCard(): JSX.Element | null {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: tenant } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });

  const show =
    user?.role !== "accountant" &&
    Boolean(tenant) &&
    Boolean(tenant?.fiscaloConfig?.prerequisitesMet) &&
    !tenant?.emisionEnabled &&
    // Grandfather existing emitters (already invoicing) + demo tenants — they're
    // already working, so don't nudge them to "activate" the provider.
    !tenant?.hasEmittedCfe &&
    !tenant?.bypassDgi &&
    // Homologación-simplificada (scratch) tenants get Fiscalo/SALKANTAI
    // registered as their software provider during the DGI postulación
    // itself — not via startFiscaloConfig — so this self-service step is
    // redundant for them.
    tenant?.onboardingSituationChoice !== "scratch";

  const { data: runningState } = useQuery({
    queryKey: ["fiscalo-config-running"],
    queryFn: getFiscaloConfigRunning,
    enabled: show,
    refetchInterval: show ? 5000 : false,
  });

  // Refresh the tenant as the automation progresses so proveedorSoftware /
  // emisionEnabled / failed flip and the card updates (or dismisses on success).
  useEffect(() => {
    if (show && runningState !== undefined) {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
    }
  }, [runningState?.running, show, queryClient]);

  const startMutation = useMutation({
    mutationFn: startFiscaloConfig,
    onSuccess: (res) => {
      if (!res.accepted) {
        toast.error(res.message ?? "No pudimos activar. Probá de nuevo.");
        return;
      }
      toast.success("Configurando en DGI… Puede tardar 1-3 minutos.");
      void queryClient.invalidateQueries({ queryKey: ["fiscalo-config-running"] });
    },
    onError: () => toast.error("No pudimos activar. Probá de nuevo."),
  });

  if (!show) return null;

  const running = Boolean(runningState?.running) || startMutation.isPending;
  const failed = tenant?.fiscaloConfig?.failed ?? null;

  if (running) {
    return (
      <div className="flex items-center gap-3 rounded-[var(--fx-r-md)] border border-[var(--fx-accent-300)] bg-[rgba(45,97,73,0.06)] px-5 py-4">
        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[var(--fx-accent-700)]" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[var(--fx-ink-900)]">
            Activando Fiscalo en DGI…
          </div>
          <div className="mt-0.5 text-xs text-[var(--fx-ink-500)]">
            Registrando a Fiscalo como proveedor. Puede tardar 1-3 minutos — esta vista se actualiza sola.
          </div>
        </div>
      </div>
    );
  }

  const isError = Boolean(failed);

  return (
    <div
      className={`flex flex-col gap-3 rounded-[var(--fx-r-md)] border px-5 py-4 sm:flex-row sm:items-center ${
        isError
          ? "border-[#E7C3AC] bg-[#FBEDE4]"
          : "border-[var(--fx-accent-300)] bg-[rgba(45,97,73,0.06)]"
      }`}
    >
      <span
        aria-hidden
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white ${
          isError ? "text-[#B4531F]" : "text-[var(--fx-accent-700)]"
        }`}
      >
        {isError ? <AlertTriangle className="h-4 w-4" /> : <Rocket className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[var(--fx-ink-900)]">
          {isError ? "No pudimos activar Fiscalo" : "Activá Fiscalo para emitir"}
        </div>
        <div className="mt-0.5 text-xs text-[var(--fx-ink-500)]">
          {isError
            ? "Reintentá el registro en DGI. Si sigue fallando, escribinos."
            : "Registramos a Fiscalo como tu proveedor en DGI. Es el último paso para poder emitir."}
        </div>
      </div>
      <button
        type="button"
        className="fx-btn fx-btn-accent shrink-0"
        disabled={startMutation.isPending}
        onClick={() => startMutation.mutate()}
      >
        {isError ? "Reintentar" : "Activar Fiscalo"}
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
